import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AdminAuthService } from '../../services/admin-auth.service';

/**
 * Interface for the transformed option
 */
export interface OptionItem {
  id: number;
  name: string;
}

/**
 * String Helper Utility Class
 * Provides utility functions for filtering and transforming assets status data
 */
export class StringHelper {
  /**
   * Get options from assets status data by type
   * Filters by the provided type name and transforms to {id, name} format
   * 
   * @param adminAuthService - Instance of AdminAuthService to access assets status data
   * @param type - The type name to filter by (e.g., 'hospitalType', 'state', 'city')
   * @returns Observable that emits array of options in {id, name} format or empty array
   * 
   * @example
   * // Get hospital type options
   * StringHelper.getOptionsByType(adminAuthService, 'hospitalType').subscribe(options => {
   *   console.log(options); // [{id: 112, name: "Multi-Specialty Hospital"}, ...]
   * });
   * 
   * @example
   * // Get state options
   * StringHelper.getOptionsByType(adminAuthService, 'state').subscribe(options => {
   *   console.log(options); // [{id: 1, name: "Maharashtra"}, ...]
   * });
   */
  static getOptionsByType(
    adminAuthService: AdminAuthService,
    type: string
  ): Observable<OptionItem[]> {
    if (!type || typeof type !== 'string') {
      console.warn('Invalid type provided to getOptionsByType');
      return of([]);
    }

    return adminAuthService.getAssetsStatusData().pipe(
      map(data => {
        // console.log(`🔍 StringHelper: Looking for type "${type}" in data:`, data);
        
        if (!data) {
          return [];
        }

        // Handle different response structures
        let dataArray: any[] = [];
        
        // If data is an array, use it directly
        if (Array.isArray(data)) {
          dataArray = data;
          // console.log(`📦 Data is an array with ${dataArray.length} items`);
        } 
        // If data has a data property that's an array
        else if (data.data && Array.isArray(data.data)) {
          dataArray = data.data;
          // console.log(`📦 Data has .data property with ${dataArray.length} items`);
        }
        // If data has a result property that's an array
        else if (data.result && Array.isArray(data.result)) {
          dataArray = data.result;
          // console.log(`📦 Data has .result property with ${dataArray.length} items`);
        }
        // If data is an object with array values
        else if (typeof data === 'object') {
          // Try to find array values in the object
          const keys = Object.keys(data);
          for (const key of keys) {
            if (Array.isArray(data[key])) {
              dataArray = data[key];
              // console.log(`📦 Found array in key "${key}" with ${dataArray.length} items`);
              break;
            }
          }
        }

        if (dataArray.length === 0) {
          return [];
        }
        
     

        // Normalize the type name for comparison (handle various formats)
        const normalizedType = type.toLowerCase().trim();
        const typeVariations = [
          normalizedType,
          normalizedType.replace(/_/g, ''),
          normalizedType.replace(/-/g, ''),
          normalizedType.replace(/_/g, '-'),
          normalizedType.replace(/-/g, '_'),
          normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1),
          normalizedType.toUpperCase()
        ];

        // Check if this is a flat structure (items have both 'name' and 'value' properties)
        // Example: [{id: 1, name: 'hospitaltype', value: 'Clinic/OPD'}, ...]
        const firstItem = dataArray[0];
        const isFlatStructure = firstItem && 
                               typeof firstItem === 'object' &&
                               'name' in firstItem && 
                               'value' in firstItem;
        
        // console.log(`🔍 Structure check - isFlatStructure: ${isFlatStructure}, firstItem:`, firstItem);

        let valuesArray: any[] = [];

        if (isFlatStructure) {
          // Flat structure: Filter items where name matches the type
          // console.log(`📦 Detected flat structure - filtering items where name matches "${normalizedType}"`);
          
          valuesArray = dataArray.filter((item: any) => {
            const itemName = (item?.name || item?.key || item?.type || item?.field || '').toLowerCase();
            return typeVariations.some(variation => itemName === variation);
          });

          // console.log(`🔢 Found ${valuesArray.length} items matching type "${type}":`, valuesArray);
        } else {
          // Nested structure: Find item with matching name, then get its values
          
          const typeData = dataArray.find((item: any) => {
            const itemName = item?.name?.toLowerCase() || 
                            item?.key?.toLowerCase() || 
                            item?.type?.toLowerCase() || 
                            item?.field?.toLowerCase() || 
                            '';
            
            return typeVariations.some(variation => itemName === variation);
          });

          if (!typeData) {
            console.warn(`Type "${type}" not found in assets status data. Available types:`, 
              dataArray.map((item: any) => item?.name || item?.key || 'unknown'));
            return [];
          }

          // Get values array - handle different property names
          if (Array.isArray(typeData.values)) {
            valuesArray = typeData.values;
          } else if (Array.isArray(typeData.data)) {
            valuesArray = typeData.data;
          } else if (Array.isArray(typeData.items)) {
            valuesArray = typeData.items;
          } else if (Array.isArray(typeData)) {
            valuesArray = typeData;
          }

        }

        if (valuesArray.length === 0) {
          console.warn(`No values found for type "${type}"`);
          return [];
        }
        // Transform values to {id, name} format
        const options: OptionItem[] = valuesArray.map((value: any, index: number) => {
          // If value is an object with id and name/labelName, use those
          if (typeof value === 'object' && value !== null) {
            // For flat structure, use 'value' property as the display name
            // For nested structure, use 'name' or other properties
            const displayName = value.value || 
                               value.labelName || 
                               value.label || 
                               value.name || 
                               value.displayName || 
                               value.text ||
                               String(value);
            
            return {
              id: value.id || value.valueId || value.code || (index + 1),
              name: displayName
            };
          }
          // If value is a string or number, create object with id and name
          return {
            id: typeof value === 'number' ? value : (index + 1),
            name: String(value)
          };
        });

        return options;
      }),
      catchError(error => {
        console.error(`❌ Error getting options for type "${type}":`, error);
        return of([]);
      })
    );
  }

  /**
   * Get a single option by type and id
   * 
   * @param adminAuthService - Instance of AdminAuthService to access assets status data
   * @param type - The type name to filter by
   * @param id - The id of the option to find
   * @returns Observable that emits the option or null if not found
   * 
   * @example
   * StringHelper.getOptionById(adminAuthService, 'hospitalType', 112).subscribe(option => {
   *   if (option) {
   *   }
   * });
   */
  static getOptionById(
    adminAuthService: AdminAuthService,
    type: string,
    id: number
  ): Observable<OptionItem | null> {
    return this.getOptionsByType(adminAuthService, type).pipe(
      map(options => {
        const option = options.find(opt => opt.id === id);
        return option || null;
      })
    );
  }

  /**
   * Get option name by type and id
   * 
   * @param adminAuthService - Instance of AdminAuthService to access assets status data
   * @param type - The type name to filter by
   * @param id - The id of the option to find
   * @returns Observable that emits the option name or empty string if not found
   * 
   * @example
   * StringHelper.getOptionName(adminAuthService, 'hospitalType', 112).subscribe(name => {
   * });
   */
  static getOptionName(
    adminAuthService: AdminAuthService,
    type: string,
    id: number
  ): Observable<string> {
    return this.getOptionById(adminAuthService, type, id).pipe(
      map(option => option?.name || '')
    );
  }
}

